import {
  Alert,
  Button,
  Box,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { formatDate } from "@bw/shared/utils";
import type { TournamentPlayerEntryResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { useMyRegistrations } from "../hooks/useMyRegistrations";
import { humanizeEntryStatus } from "../utils/entryStatus";
import { TournamentPairingRequests } from "../components/TournamentPairingRequests";
import { TournamentRegistrationPayments } from "../components/TournamentRegistrationPayments";
import { TournamentParticipationSummary } from "../components/TournamentParticipationSummary";
import { TournamentTeamEntries } from "../components/TournamentTeamEntries";

/**
 * MyEntriesPage (/compete/mine?type=tournament) — the player's CROSS-TOURNAMENT entry
 * history with an exact self entry_id selector. A
 * READ-ONLY list of the token user's draw entries across EVERY tournament,
 * SERVER-ORDERED (newest-first) and rendered verbatim — the page never re-sorts,
 * re-derives a status, or computes money.
 *
 * Each row is a dense MUI list item: the tournament name (a <Link> to
 * /tournaments/{tournament_id} — the uuid is the link TARGET, never visible text),
 * a secondary line carrying the event + draw name (collapsed to ONE when
 * the two are identical — a single-draw event named after its draw must not read
 * "X · X"), the doubles partner by display name (only when present), the waitlist
 * position (ONLY when waitlisted), the seed (when present) and the registration
 * date (the shared formatDate util — em-dash for null); plus an HONEST status
 * chip whose copy is the shared humanizeEntryStatus map (registered
 * → "Registered", partner_wanted → "Needs a partner", … — NEVER the raw enum, and
 * an unknown status is surfaced verbatim, never coerced to a friendly label). No
 * id (tournament / event / draw / entry / player / partner) is ever rendered as
 * visible text.
 *
 * Honesty: loading is role="status" (NOT a fabricated empty list), error is
 * role="alert" with a retry affordance (gated on react-query isError, NOT !data),
 * and the empty state is the honest "haven't entered any tournaments" copy with a
 * link back to browse.
 *
 * The AppShell supplies no page title, so this routed page OWNS its single <h1>
 * via <Page title="My entries">.
 */

export function MyEntriesPage() {
  const { user } = useAuth();
  if (!user) return null;
  return <MyEntriesBody key={user.uid} />;
}

function MyEntriesBody() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const selections = params.getAll("entry_id");
  const hasSelection = selections.length > 0;
  const selected = selections.length === 1 &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selections[0] ?? "")
    ? selections[0]?.toLowerCase() : undefined;
  const rawPage = Number(params.get("tournament_page"));
  const page = !hasSelection && Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const query = useMyRegistrations(page, selected, !hasSelection || selected !== undefined);
  const { data, isError, refetch } = query;
  const current = query.isSuccess && query.isFetchedAfterMount && query.fetchStatus === "idle" && query.error === null;
  const entries = hasSelection ? (data?.items ?? []).filter((entry) => entry.entry_id.toLowerCase() === selected) : data?.items ?? [];
  const allParams = new URLSearchParams(params);
  allParams.delete("entry_id");
  allParams.delete("tournament_page");
  const allQuery = allParams.toString();
  const allEntries = `${location.pathname}${allQuery ? `?${allQuery}` : ""}${location.hash}`;
  function changePage(nextPage: number) {
    const next = new URLSearchParams(params);
    next.set("tournament_page", String(nextPage));
    setParams(next);
  }

  let body: React.ReactNode;
  if (hasSelection && selected === undefined) {
    body = <Alert severity="info">This entry is unavailable in your account.</Alert>;
  } else if (query.fetchStatus === "paused") {
    body = <Alert severity="info" role="status">Waiting for a connection to refresh your entries.</Alert>;
  } else if (!current && !isError) {
    body = (
      <Typography color="text.secondary" role="status">
        Loading your entries…
      </Typography>
    );
  } else if (isError) {
    body = (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button
            type="button"
            color="inherit"
            size="small"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        }
      >
        We couldn&apos;t load your entries. Please try again.
      </Alert>
    );
  } else if (hasSelection && entries.length === 0) {
    body = <Alert severity="info">This entry is unavailable in your account.</Alert>;
  } else if (data !== undefined && entries.length === 0) {
    body = (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
          <Typography color="text.secondary">
            {page > 1
              ? "No tournament entries on this page."
              : "You haven't entered any tournaments yet."}
          </Typography>
          <Button
            component={RouterLink}
            to="/compete/tournaments"
            variant="contained"
          >
            Browse tournaments
          </Button>
        </Stack>
      </Paper>
    );
  } else {
    body = (
      <List disablePadding component={Paper} variant="outlined" role="list">
        {/* SERVER-ORDERED (newest-first) — rendered verbatim, never re-sorted.
            Keyed by (entry_id + draw_id + index): a player can hold MULTIPLE
            entries (different draws across tournaments) and entry_id alone is not
            guaranteed unique across the cross-tournament list, so the index
            disambiguates to keep React keys stable + unique. */}
        {entries.map((entry, i) => (
          <EntryRow
            key={`${entry.entry_id}:${entry.draw_id}:${i}`}
            entry={entry}
          />
        ))}
      </List>
    );
  }

  return (
    <Page title="My entries" hideTitle>
      <TournamentRegistrationPayments />
      <TournamentPairingRequests />
      <TournamentTeamEntries />
      {hasSelection ? <Button component={RouterLink} to={allEntries} size="small" sx={{ mb: 2 }}>All entries</Button> : null}
      <Stack spacing={2}>{body}</Stack>
      {data && current && !hasSelection && (page > 1 || data.has_more) ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button disabled={page === 1} onClick={() => changePage(page - 1)}>
            Previous entries
          </Button>
          <Button
            disabled={!data.has_more}
            onClick={() => changePage(page + 1)}
          >
            Next entries
          </Button>
        </Stack>
      ) : null}
    </Page>
  );
}

function EntryRow({ entry }: { entry: TournamentPlayerEntryResponse }) {
  // Partner ONLY when the entry actually carries one (doubles). Prefer the human
  // display name, fall back to the plain name — NEVER the partner uuid.
  const partnerName = entry.partner_display_name ?? entry.partner_name ?? null;
  const isWaitlisted = entry.status.toLowerCase() === "waitlisted";

  // De-dup the event/draw name: a single-draw event whose draw is named after the
  // event sends event_name === draw_name → render the name ONCE, never "X · X".
  // When they genuinely differ (or draw_name is null/blank) keep BOTH — real info
  // is never suppressed. Trimmed compare so trailing whitespace can't defeat it.
  const eventName = entry.event_name?.trim() ? entry.event_name : null;
  const drawName = entry.draw_name?.trim() ? entry.draw_name : null;
  const drawIsDistinct =
    drawName != null && drawName.trim() !== (eventName ?? "").trim();
  const facts = [
    eventName,
    drawIsDistinct ? drawName : null,
    partnerName ? `with ${partnerName}` : null,
    // Waitlist position ONLY when waitlisted — a registered entry shows no
    // "waitlist"/"position" text at all. The adjacent "Waitlisted" status
    // badge supplies the waitlist context; this line carries the concrete
    // place (and is the lone "position" text on the row, so it never leaks
    // for a non-waitlisted entry).
    isWaitlisted && entry.waitlist_position != null
      ? `Position ${entry.waitlist_position}`
      : null,
    entry.seed != null ? `Seed ${entry.seed}` : null,
    `Registered ${formatDate(entry.registered_at)}`,
  ].filter((fact): fact is string => Boolean(fact));
  const tournamentName = entry.tournament_name ?? "Tournament";
  const summary = (
    <Stack spacing={1} sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
      <Chip
        size="small"
        color={isWaitlisted ? "warning" : "primary"}
        label={humanizeEntryStatus(entry.status)}
        data-status={entry.status}
        sx={{
          alignSelf: "flex-start",
          maxWidth: "100%",
          height: "auto",
          "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 },
        }}
      />
      <Typography sx={{ fontWeight: 800 }}>{tournamentName}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Stack
          component="span"
          direction="row"
          useFlexGap
          sx={{ flexWrap: "wrap", color: "text.secondary" }}
        >
          {facts.map((fact, index) => (
            <Typography
              key={`${fact}:${index}`}
              component="span"
              variant="body2"
              sx={{
                ...(index > 0 ? { "&::before": { content: '" · "' } } : {}),
              }}
            >
              {fact}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Stack>
  );

  return (
    <ListItem divider disablePadding sx={{ display: "block" }}>
      {entry.detail_url ? (
        <ListItemButton
          component={RouterLink}
          to={entry.detail_url}
          aria-label={tournamentName}
          sx={{ p: 2, alignItems: "flex-start" }}
        >
          {summary}
        </ListItemButton>
      ) : (
        <Box sx={{ p: 2 }}>{summary}</Box>
      )}
      <Stack spacing={1} sx={{ px: 2, pb: 2, minWidth: 0 }}>
        {entry.results_url ? (
          <Button
            component={RouterLink}
            to={entry.results_url}
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            Draw &amp; results
          </Button>
        ) : null}
        <TournamentParticipationSummary
          presence={entry.presence}
          liveUrl={entry.live_url}
          nextMatch={entry.next_match}
          nextMatchUrl={entry.next_match_url}
        />
      </Stack>
    </ListItem>
  );
}
